import React, { useState, useEffect } from 'react';
import { api } from '../../App';
import toast from 'react-hot-toast';
import { PlatformTag, ApplicationAnswers, AiCriteriaModal } from './CandidateExtras';

const STAGES = [
  ['applied', 'Applied', 'bg-slate-400'],
  ['screening', 'Screening', 'bg-sky-500'],
  ['interview', 'Interview', 'bg-amber-500'],
  ['offer', 'Offer', 'bg-violet-500'],
  ['hired', 'Hired', 'bg-emerald-500'],
  ['rejected', 'Rejected', 'bg-rose-400'],
];
const EMPTY = { name: '', email: '', phone: '', years_experience: '', closing_rate: '', solar_experience: false, door_to_door: false, has_vehicle: false, bilingual: false, full_time: false, notes: '', job_id: '' };
const recColor = (r) => r?.startsWith('HIRE') ? 'bg-emerald-100 text-emerald-700' : r === 'INTERVIEW' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600';
const scoreBar = (s) => s >= 75 ? 'bg-emerald-500' : s >= 55 ? 'bg-primary' : s >= 40 ? 'bg-amber-500' : 'bg-slate-400';

export default function Pipeline() {
  const [candidates, setCandidates] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [team, setTeam] = useState([]);
  const [jobFilter, setJobFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [selected, setSelected] = useState([]);
  const [hiring, setHiring] = useState(false);
  const [hiredResult, setHiredResult] = useState(null);
  const [detail, setDetail] = useState(null);
  const [showCriteria, setShowCriteria] = useState(false);

  const load = async () => {
    try {
      const [c, j, t] = await Promise.all([
        api.get(`/api/hiring/candidates${jobFilter ? `?job_id=${jobFilter}` : ''}`),
        api.get('/api/hiring/jobs'),
        api.get('/api/hiring/team'),
      ]);
      setCandidates(c.data.candidates || []);
      setJobs(j.data.jobs || []);
      setTeam(t.data.team || []);
    } catch { toast.error('Failed to load pipeline'); }
  };
  useEffect(() => { load(); }, [jobFilter]); // eslint-disable-line

  const addCandidate = async (e) => {
    e.preventDefault();
    try {
      const r = await api.post('/api/hiring/candidates', { ...form, job_title: jobs.find(j => j.id === form.job_id)?.title });
      toast.success(`${r.data.name} scored ${r.data.score}/100 — ${r.data.recommendation}`);
      setForm(EMPTY); setShowForm(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to add candidate'); }
  };
  const removeCandidate = async (id) => {
    try { await api.delete(`/api/hiring/candidates/${id}`); setDetail(null); load(); } catch { toast.error('Delete failed'); }
  };
  const moveStage = async (id, stage) => {
    try { await api.patch(`/api/hiring/candidates/${id}/stage`, { stage }); load(); setDetail(d => d && d.id === id ? { ...d, stage } : d); }
    catch { toast.error('Could not move candidate'); }
  };
  const assign = async (id, assigned_to) => {
    try { await api.patch(`/api/hiring/candidates/${id}/assign`, { assigned_to }); load(); setDetail(d => d && d.id === id ? { ...d, assigned_to } : d); }
    catch { toast.error('Assign failed'); }
  };
  const toggleSel = (id) => setSelected(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
  const massHire = async () => {
    if (!selected.length) return toast.error('Select candidates first');
    setHiring(true);
    try {
      const r = await api.post('/api/hiring/mass-hire', { candidate_ids: selected });
      setHiredResult(r.data); setSelected([]); load();
      toast.success(`${r.data.hired.length} team member(s) onboarded!`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Mass hire failed'); }
    setHiring(false);
  };

  const byStage = (s) => candidates.filter(c => (c.stage || 'applied') === s);

  return (
    <div className="space-y-5" data-testid="pipeline-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <select value={jobFilter} onChange={e => setJobFilter(e.target.value)} className="input-field text-xs w-48" data-testid="pipeline-job-filter">
            <option value="">All jobs</option>
            {jobs.map(j => <option key={j.id} value={j.id}>{j.title}</option>)}
          </select>
          <span className="text-xs text-text-muted">{candidates.length} applicant(s)</span>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowCriteria(true)} className="btn-secondary text-sm" data-testid="ai-criteria-btn"><i className="fa-solid fa-robot mr-1.5" />AI Review Criteria</button>
          <button onClick={() => setShowForm(!showForm)} className="btn-secondary text-sm" data-testid="add-candidate-btn"><i className="fa-solid fa-plus mr-1.5" />Add Candidate</button>
          <button onClick={massHire} disabled={hiring || !selected.length}
            className="px-4 py-2 rounded-xl bg-gradient-to-r from-emerald-600 to-primary text-white text-sm font-bold disabled:opacity-50 hover:brightness-110 transition-all" data-testid="mass-hire-btn">
            {hiring ? <i className="fa-solid fa-circle-notch fa-spin mr-1.5" /> : <i className="fa-solid fa-bolt mr-1.5" />}Mass Hire ({selected.length})
          </button>
        </div>
      </div>

      {showForm && (
        <form onSubmit={addCandidate} className="bg-surface rounded-2xl border border-border p-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="candidate-form">
          <input required placeholder="Full name *" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="input-field" data-testid="candidate-name-input" />
          <input placeholder="Email" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="input-field" data-testid="candidate-email-input" />
          <input placeholder="Phone" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="input-field" />
          <input placeholder="Years of sales experience" type="number" min="0" step="0.5" value={form.years_experience} onChange={e => setForm({ ...form, years_experience: e.target.value })} className="input-field" data-testid="candidate-years-input" />
          <input placeholder="Closing rate %" type="number" min="0" max="100" value={form.closing_rate} onChange={e => setForm({ ...form, closing_rate: e.target.value })} className="input-field" />
          <select value={form.job_id} onChange={e => setForm({ ...form, job_id: e.target.value })} className="input-field" data-testid="candidate-job-select">
            <option value="">No job / general pool</option>
            {jobs.map(j => <option key={j.id} value={j.id}>{j.title}</option>)}
          </select>
          <div className="sm:col-span-2 lg:col-span-3 flex flex-wrap gap-4">
            {[['solar_experience', 'Solar experience'], ['door_to_door', 'Door-to-door'], ['has_vehicle', 'Has vehicle'], ['bilingual', 'Bilingual'], ['full_time', 'Full-time']].map(([k, l]) => (
              <label key={k} className="flex items-center gap-2 text-sm text-text-secondary cursor-pointer">
                <input type="checkbox" checked={form[k]} onChange={e => setForm({ ...form, [k]: e.target.checked })} className="accent-emerald-600 w-4 h-4" data-testid={`candidate-${k}-check`} />{l}
              </label>
            ))}
          </div>
          <div className="sm:col-span-2 lg:col-span-3">
            <button type="submit" className="btn-primary text-sm" data-testid="candidate-submit-btn"><i className="fa-solid fa-calculator mr-1.5" />Score &amp; Add</button>
          </div>
        </form>
      )}

      {/* Kanban board */}
      <div className="grid gap-3 lg:grid-cols-3 xl:grid-cols-6" data-testid="ats-board">
        {STAGES.map(([key, label, color]) => (
          <div key={key} className="bg-surface-alt/40 rounded-2xl border border-border p-2.5 min-h-[120px]" data-testid={`stage-col-${key}`}>
            <div className="flex items-center justify-between px-1.5 mb-2">
              <span className="flex items-center gap-1.5 text-xs font-bold uppercase text-text-secondary"><span className={`w-2 h-2 rounded-full ${color}`} />{label}</span>
              <span className="text-[10px] font-bold text-text-muted">{byStage(key).length}</span>
            </div>
            <div className="space-y-2">
              {byStage(key).map(c => (
                <div key={c.id} className="bg-surface rounded-xl border border-border p-3 hover:shadow-sm transition-all" data-testid="candidate-card">
                  <div className="flex items-start justify-between gap-1">
                    <button onClick={() => setDetail(c)} className="text-left min-w-0 flex-1" data-testid="open-candidate-btn">
                      <p className="text-sm font-semibold text-text-primary truncate">{c.name}</p>
                      <p className="text-[10px] text-text-muted truncate">{c.job_title || c.source || 'General pool'}</p>
                      <div className="mt-1"><PlatformTag candidate={c} /></div>
                    </button>
                    {c.status !== 'hired' && (
                      <input type="checkbox" checked={selected.includes(c.id)} onChange={() => toggleSel(c.id)} className="accent-emerald-600 w-3.5 h-3.5 mt-0.5" data-testid="candidate-select-check" />
                    )}
                  </div>
                  <div className="mt-2 h-1.5 rounded-full bg-surface-alt overflow-hidden"><div className={`h-full ${scoreBar(c.score)}`} style={{ width: `${c.score}%` }} /></div>
                  <div className="flex items-center justify-between mt-2 gap-1">
                    <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase ${recColor(c.recommendation)}`}>{c.score}·{c.recommendation}</span>
                    <select value={c.stage || 'applied'} onChange={e => moveStage(c.id, e.target.value)} className="text-[10px] bg-surface-alt border border-border rounded-md px-1 py-0.5 text-text-secondary" data-testid="card-stage-select">
                      {STAGES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                    </select>
                  </div>
                  {c.assigned_to && <p className="text-[9px] text-text-muted mt-1"><i className="fa-solid fa-user-tie mr-1" />{c.assigned_to}</p>}
                </div>
              ))}
              {byStage(key).length === 0 && <p className="text-[10px] text-text-muted text-center py-3 opacity-60">Empty</p>}
            </div>
          </div>
        ))}
      </div>

      {/* Candidate detail modal */}
      {detail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={() => setDetail(null)} data-testid="candidate-detail-modal">
          <div className="bg-surface rounded-2xl border border-border max-w-lg w-full max-h-[88vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="p-6 space-y-4">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-heading text-lg font-bold text-text-primary">{detail.name}</h3>
                  <p className="text-xs text-text-muted">{detail.email || 'no email'} · {detail.phone || 'no phone'}</p>
                  <p className="text-xs text-text-muted mt-0.5">{detail.job_title || 'General pool'} · via {detail.source || 'manual'}</p>
                  <div className="mt-1.5 flex items-center gap-1.5" data-testid="detail-platform"><span className="text-[10px] font-bold uppercase text-text-muted">Source platform</span><PlatformTag candidate={detail} size="md" />{!detail.platform && !detail.channel && <span className="text-[10px] text-text-muted">Added manually</span>}</div>
                </div>
                <button onClick={() => setDetail(null)} className="text-text-muted hover:text-text-primary p-1"><i className="fa-solid fa-xmark" /></button>
              </div>

              <div className="flex items-center gap-3">
                <div className="flex-1">
                  <div className="flex justify-between text-[10px] font-bold text-text-muted mb-1"><span>RUBRIC SCORE</span><span>{detail.score}/100</span></div>
                  <div className="h-2 rounded-full bg-surface-alt overflow-hidden"><div className={`h-full ${scoreBar(detail.score)}`} style={{ width: `${detail.score}%` }} /></div>
                </div>
                <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase ${recColor(detail.recommendation)}`}>{detail.recommendation}</span>
              </div>

              {detail.screening_score != null && (
                <div className="text-xs text-text-secondary bg-surface-alt rounded-xl p-3">
                  <span className="font-bold">Screening score: {detail.screening_score}/100</span>
                  {(detail.screening_detail || []).map((d, i) => <div key={i} className="flex justify-between mt-1 text-[11px] text-text-muted"><span className="truncate mr-2">{d.question}</span><span>{d.earned}/{d.max}</span></div>)}
                </div>
              )}

              <div className="grid grid-cols-2 gap-2">
                <label className="text-[10px] font-bold uppercase text-text-muted col-span-2">Move to stage
                  <select value={detail.stage || 'applied'} onChange={e => moveStage(detail.id, e.target.value)} className="input-field text-sm mt-1" data-testid="detail-stage-select">
                    {STAGES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                  </select>
                </label>
                <label className="text-[10px] font-bold uppercase text-text-muted col-span-2">Assign recruiter / manager
                  <select value={detail.assigned_to || ''} onChange={e => assign(detail.id, e.target.value)} className="input-field text-sm mt-1" data-testid="detail-assign-select">
                    <option value="">Unassigned</option>
                    {team.map(t => <option key={t.email} value={t.name}>{t.name} ({t.role})</option>)}
                  </select>
                </label>
              </div>

              {/* Applicant responses */}
              <ApplicationAnswers candidate={detail} />

              {/* Resume + LinkedIn */}
              <ResumePanel candidate={detail} onDone={(r) => setDetail(d => ({ ...d, resume_summary: r }))} />

              {/* AI review */}
              <AiReview candidate={detail} onDone={(r) => setDetail(d => ({ ...d, ai_review: r }))} />

              {/* Notes */}
              <Notes candidate={detail} onAdd={(note) => setDetail(d => ({ ...d, notes_log: [...(d.notes_log || []), note] }))} />

              <div className="flex justify-between pt-2">
                <button onClick={() => removeCandidate(detail.id)} className="text-rose-600 text-xs font-semibold hover:underline" data-testid="candidate-delete-btn"><i className="fa-solid fa-trash-can mr-1" />Delete</button>
                <button onClick={() => setDetail(null)} className="btn-secondary text-xs">Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showCriteria && <AiCriteriaModal onClose={() => setShowCriteria(false)} />}
      {hiredResult && <HiredModal result={hiredResult} onClose={() => setHiredResult(null)} />}
    </div>
  );
}

function ResumePanel({ candidate, onDone }) {
  const [loading, setLoading] = useState(false);
  const s = candidate.resume_summary;
  const hasResume = !!candidate.resume_asset_id || !!candidate.resume_text;
  const run = async () => {
    setLoading(true);
    try { const r = await api.post(`/api/hiring/candidates/${candidate.id}/resume-summary`); onDone(r.data); toast.success('Resume analyzed'); }
    catch (err) { toast.error(err.response?.data?.detail || 'Resume analysis failed'); }
    setLoading(false);
  };
  const openResume = async () => {
    try {
      const r = await api.get(`/api/hiring/candidates/${candidate.id}/resume`, { responseType: 'blob' });
      window.open(URL.createObjectURL(r.data), '_blank');
    } catch { toast.error('Could not open resume'); }
  };
  if (!hasResume && !candidate.linkedin_url) {
    return <div className="bg-surface-alt rounded-xl p-3 text-[11px] text-text-muted" data-testid="resume-panel"><i className="fa-regular fa-file mr-1.5" />No resume or LinkedIn profile submitted.</div>;
  }
  return (
    <div className="bg-surface-alt rounded-xl p-3" data-testid="resume-panel">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <span className="text-[10px] font-bold uppercase text-text-muted"><i className="fa-regular fa-file-lines mr-1" />Resume & Profile</span>
        <div className="flex gap-1.5">
          {candidate.linkedin_url && <a href={candidate.linkedin_url} target="_blank" rel="noreferrer" className="btn-secondary text-[11px] py-1" data-testid="candidate-linkedin-link"><i className="fa-brands fa-linkedin mr-1" />LinkedIn</a>}
          {candidate.resume_asset_id && <button onClick={openResume} className="btn-secondary text-[11px] py-1" data-testid="view-resume-btn"><i className="fa-solid fa-file-pdf mr-1" />View PDF</button>}
          {hasResume && <button onClick={run} disabled={loading} className="btn-secondary text-[11px] py-1" data-testid="resume-summary-btn">
            {loading ? <i className="fa-solid fa-circle-notch fa-spin mr-1" /> : <i className="fa-solid fa-wand-magic-sparkles mr-1" />}{s ? 'Re-analyze' : 'AI analyze resume'}
          </button>}
        </div>
      </div>
      {candidate.resume_filename && <p className="text-[10px] text-text-muted mt-1 truncate">{candidate.resume_filename}</p>}
      {s && (
        <div className="mt-2 text-xs text-text-secondary space-y-1.5" data-testid="resume-summary-result">
          {s.fit_score != null && <p><span className="font-bold text-primary">Resume fit: {s.fit_score}/100</span> · Verdict: {s.verdict}{s.years_sales_experience != null ? ` · ${s.years_sales_experience} yrs sales` : ''}{s.solar_experience ? ' · solar background' : ''}</p>}
          {s.summary && <p className="text-text-muted">{s.summary}</p>}
          {s.highlights?.length > 0 && <p><span className="font-semibold text-emerald-600">Highlights:</span> {s.highlights.join(', ')}</p>}
          {s.concerns?.length > 0 && <p><span className="font-semibold text-amber-600">Concerns:</span> {s.concerns.join(', ')}</p>}
        </div>
      )}
    </div>
  );
}

function AiReview({ candidate, onDone }) {
  const [loading, setLoading] = useState(false);
  const review = candidate.ai_review;
  const run = async () => {
    setLoading(true);
    try { const r = await api.post(`/api/hiring/candidates/${candidate.id}/ai-review`); onDone(r.data); toast.success('AI review ready'); }
    catch (err) { toast.error(err.response?.data?.detail || 'AI review failed'); }
    setLoading(false);
  };
  return (
    <div className="bg-surface-alt rounded-xl p-3">
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-bold uppercase text-text-muted"><i className="fa-solid fa-robot mr-1" />AI Candidate Review <span className="normal-case font-normal">(answers + resume vs your criteria)</span></span>
        <button onClick={run} disabled={loading} className="btn-secondary text-[11px] py-1" data-testid="ai-review-btn">
          {loading ? <i className="fa-solid fa-circle-notch fa-spin mr-1" /> : <i className="fa-solid fa-wand-magic-sparkles mr-1" />}{review ? 'Re-run' : 'Run AI Review'}
        </button>
      </div>
      {review && (
        <div className="mt-2 text-xs text-text-secondary space-y-1.5" data-testid="ai-review-result">
          <p><span className="font-bold text-primary">AI Fit Score: {review.ai_score}/100</span> · Verdict: {review.verdict}</p>
          {review.summary && <p className="text-text-muted">{review.summary}</p>}
          {review.strengths?.length > 0 && <p><span className="font-semibold text-emerald-600">Strengths:</span> {review.strengths.join(', ')}</p>}
          {review.concerns?.length > 0 && <p><span className="font-semibold text-amber-600">Concerns:</span> {review.concerns.join(', ')}</p>}
          {review.answer_notes?.length > 0 && <div data-testid="ai-review-answer-notes"><span className="font-semibold text-sky-600">On their answers:</span><ul className="list-disc ml-4 mt-0.5">{review.answer_notes.map((n, i) => <li key={i}>{n}</li>)}</ul></div>}
          {review.reviewed_answers != null && <p className="text-[10px] text-text-muted">Reviewed {review.reviewed_answers} screening answer(s){review.used_resume ? ' + resume' : ' · no resume on file'}</p>}
          {review.criteria_used && <details className="text-[10px] text-text-muted"><summary className="cursor-pointer">Criteria used</summary><p className="mt-1 whitespace-pre-wrap">{review.criteria_used}</p></details>}
        </div>
      )}
    </div>
  );
}

function Notes({ candidate, onAdd }) {
  const [text, setText] = useState('');
  const add = async () => {
    if (!text.trim()) return;
    try { const r = await api.post(`/api/hiring/candidates/${candidate.id}/note`, { text }); onAdd(r.data); setText(''); }
    catch { toast.error('Could not add note'); }
  };
  return (
    <div>
      <p className="text-[10px] font-bold uppercase text-text-muted mb-1">Notes</p>
      <div className="space-y-1.5 mb-2 max-h-32 overflow-y-auto">
        {(candidate.notes_log || []).length === 0 && <p className="text-[11px] text-text-muted opacity-60">No notes yet.</p>}
        {(candidate.notes_log || []).map((n, i) => (
          <div key={i} className="text-[11px] text-text-secondary bg-surface-alt rounded-lg px-2.5 py-1.5"><span>{n.text}</span><span className="text-text-muted block text-[9px] mt-0.5">{n.by} · {new Date(n.at).toLocaleString()}</span></div>
        ))}
      </div>
      <div className="flex gap-2">
        <input value={text} onChange={e => setText(e.target.value)} placeholder="Add a note…" className="input-field text-xs flex-1" data-testid="note-input" />
        <button onClick={add} className="btn-secondary text-xs" data-testid="add-note-btn">Add</button>
      </div>
    </div>
  );
}

function HiredModal({ result, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" data-testid="hired-modal">
      <div className="bg-surface rounded-2xl border border-border max-w-lg w-full p-6 max-h-[85vh] overflow-y-auto">
        <h3 className="font-heading text-lg font-bold text-text-primary mb-1"><i className="fa-solid fa-circle-check text-emerald-500 mr-2" />{result.hired.length} team member(s) onboarded</h3>
        <p className="text-xs text-amber-600 font-semibold mb-4"><i className="fa-solid fa-triangle-exclamation mr-1" />Copy these credentials now — passwords are shown only once.</p>
        <div className="space-y-3">
          {result.hired.map(h => (
            <div key={h.email} className="p-4 rounded-xl bg-surface-alt border border-border" data-testid="hired-credential-row">
              <p className="text-sm font-semibold text-text-primary">{h.name} <span className="text-[10px] font-bold uppercase text-primary ml-1">{h.role}</span></p>
              <p className="text-xs font-mono text-text-secondary mt-1">{h.email} / {h.password}</p>
            </div>
          ))}
          {result.skipped?.map((sk, i) => <p key={i} className="text-xs text-text-muted"><i className="fa-solid fa-circle-minus mr-1" />{sk.name || sk.id}: {sk.reason}</p>)}
        </div>
        <div className="flex gap-2 mt-5">
          <button onClick={() => { navigator.clipboard.writeText(result.hired.map(h => `${h.name}: ${h.email} / ${h.password}`).join('\n')); toast.success('Copied'); }} className="btn-secondary text-xs" data-testid="copy-credentials-btn"><i className="fa-solid fa-copy mr-1.5" />Copy All</button>
          <button onClick={onClose} className="btn-primary text-xs" data-testid="close-hired-modal-btn">Done</button>
        </div>
      </div>
    </div>
  );
}
